/** @file Shares repository — unauth-readable top-level `shares/{shareId}` with play-doc mirror. */
import {
  arrayRemove,
  arrayUnion,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { nanoid } from "nanoid";
import { doc } from "firebase/firestore";
import { getDb } from "./firebase.js";
import { sharesCol, shareDoc } from "./db.js";

/** @typedef {import("../types/schemas.js").ShareDoc} ShareDoc */

/** Length of generated share IDs (nanoid alphabet, ~71 bits entropy). */
export const SHARE_ID_LENGTH = 12;

/**
 * Create a new share for a play. Writes `shares/{shareId}` and mirrors shareId
 * into `users/{ownerId}/plays/{playId}.publicShareIds` atomically via writeBatch.
 * @param {string} ownerId
 * @param {string} playId
 * @returns {Promise<string>} new shareId
 */
export async function createShare(ownerId, playId) {
  const db = getDb();
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const shareId = nanoid(SHARE_ID_LENGTH);
  const batch = writeBatch(db);
  const sRef = doc(db, "shares", shareId);
  const pRef = doc(db, "users", ownerId, "plays", playId);
  batch.set(sRef, {
    shareId,
    ownerId,
    playId,
    createdAt: serverTimestamp(),
    revoked: false,
  });
  batch.update(pRef, {
    publicShareIds: arrayUnion(shareId),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
  return shareId;
}

/**
 * @param {string} shareId
 * @returns {Promise<ShareDoc | null>}
 */
export async function getShare(shareId) {
  const snap = await getDoc(shareDoc(shareId));
  return snap.exists() ? snap.data() : null;
}

/**
 * Revoke a share: flips `revoked=true` and removes id from play mirror array.
 * @param {string} shareId
 * @returns {Promise<void>}
 */
export async function revokeShare(shareId) {
  const db = getDb();
  if (!db) {
    throw new Error("Firestore not initialized");
  }
  const existing = await getShare(shareId);
  if (!existing) {
    throw new Error("Share not found");
  }
  const batch = writeBatch(db);
  const sRef = doc(db, "shares", shareId);
  const pRef = doc(db, "users", existing.ownerId, "plays", existing.playId);
  batch.update(sRef, { revoked: true });
  batch.update(pRef, {
    publicShareIds: arrayRemove(shareId),
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * @param {string} ownerId
 * @param {string} playId
 * @returns {Promise<ShareDoc[]>}
 */
export async function listSharesByPlay(ownerId, playId) {
  const q = query(
    sharesCol(),
    where("ownerId", "==", ownerId),
    where("playId", "==", playId),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}
